import { Plus, LogIn, ArrowRight, Sparkles } from "lucide-react";

const OPTIONS = [
  {
    id: "current",
    label: "A · Current",
    note: "#0F0F14 — near-black with cool tint.",
    style: { background: "#0F0F14" },
  },
  {
    id: "lifted",
    label: "B · Lifted dark",
    note: "Same vibe, brighter base. Easier on eyes.",
    style: { background: "#1A1726" },
  },
  {
    id: "twilight",
    label: "C · Twilight gradient",
    note: "Top-to-bottom fade. Warmer, more depth.",
    style: {
      background:
        "radial-gradient(120% 80% at 50% 0%, #2A1F3D 0%, #1A1726 45%, #14111E 100%)",
    },
  },
  {
    id: "warm",
    label: "D · Warm rose night",
    note: "Pink-purple wash. Most inviting, most 'couples'.",
    style: {
      background:
        "radial-gradient(140% 100% at 30% 0%, rgba(255,77,141,0.12) 0%, transparent 55%), radial-gradient(120% 90% at 80% 100%, rgba(123,97,255,0.14) 0%, transparent 55%), #1A1626",
    },
  },
];

function Sample({ opt }) {
  return (
    <div
      className="rounded-3xl overflow-hidden border border-white/10"
      style={opt.style}
      data-testid={`bg-sample-${opt.id}`}
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF]">
          {opt.label}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
          preview
        </span>
      </div>

      <div className="px-5 pb-5">
        {/* Hero block */}
        <h1
          className="font-display italic text-white text-3xl md:text-4xl leading-[1.05] mb-2"
          style={{ letterSpacing: "-0.02em" }}
        >
          Pick a{" "}
          <span
            style={{
              background: "linear-gradient(135deg, #FF4D8D, #7B61FF)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            vibe.
          </span>
        </h1>
        <p className="text-sm text-[#C9C7D9] mb-4">
          From cute to chaotic, see how well you really know each other.
        </p>

        {/* Two action chips */}
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <div className="rounded-xl border border-[#FF4D8D]/40 bg-[#FF4D8D]/[0.06] p-3">
            <div className="flex items-center gap-2 text-white text-sm font-bold mb-0.5">
              <Plus className="w-3.5 h-3.5" /> Create a room
            </div>
            <p className="text-[11px] text-[#C9C7D9] leading-tight">
              Start a new game.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="flex items-center gap-2 text-white text-sm font-bold mb-0.5">
              <LogIn className="w-3.5 h-3.5" /> Join with code
            </div>
            <p className="text-[11px] text-[#C9C7D9] leading-tight">
              Got one from bae.
            </p>
          </div>
        </div>

        {/* Mini bundle banner */}
        <div
          className="rounded-2xl border-2 px-4 py-3 mb-3 flex items-center gap-3 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,77,141,0.22), rgba(123,97,255,0.22)), rgba(15,15,20,0.55)",
            borderColor: "rgba(255,77,141,0.7)",
            boxShadow: "0 0 30px rgba(255,77,141,0.3)",
          }}
        >
          <span className="text-2xl">🔓</span>
          <div className="flex-1 min-w-0">
            <span className="inline-block text-[8px] font-bold uppercase tracking-[0.25em] bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] text-white px-1.5 py-0.5 rounded-full mb-1">
              Best Value
            </span>
            <p className="font-bold text-white text-sm leading-tight">
              Unlock all packs
            </p>
          </div>
          <span className="font-display-tight font-bold text-white text-lg">
            $4.99
          </span>
        </div>

        {/* A pack tile to test contrast */}
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3">
          <span className="text-2xl">🔥</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-white text-sm leading-tight">
              Truth or Dare
            </p>
            <p className="text-[11px] text-[#C9C7D9] leading-tight">
              Free · start here.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-white/60" />
        </div>

        {/* Note */}
        <p className="text-[11px] text-[#C9C7D9]/80 mt-4 italic">
          {opt.note}
        </p>
      </div>
    </div>
  );
}

export default function BackgroundPreview() {
  return (
    <div className="min-h-screen p-6 sm:p-10" style={{ background: "#0a090f" }}>
      <div className="max-w-7xl mx-auto mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-white text-2xl font-bold tracking-tight">
              Background preview
            </h2>
            <p className="text-sm text-[#C9C7D9]">
              Pick A, B, C or D — same content, different background.
            </p>
          </div>
        </div>
        <a
          href="/"
          className="text-xs uppercase tracking-[0.2em] font-bold text-[#C9C7D9] hover:text-white"
        >
          ← Back home
        </a>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {OPTIONS.map((o) => (
          <Sample key={o.id} opt={o} />
        ))}
      </div>

      <p className="text-center text-sm text-[#C9C7D9] mt-10">
        Tell me which one and I'll wire it across the whole app.
      </p>
    </div>
  );
}
